import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CreditCard,
  Plus,
  Pencil,
  Trash2,
  X,
  Loader2,
  Save,
  Search,
  Filter,
  AlertTriangle,
  Ticket,
  Sparkles,
  Eye,
  EyeOff,
  CalendarDays,
  Tag,
  RefreshCw,
  CheckCircle2,
  PlusCircle,
  Database,
} from 'lucide-react';
import { Alert, AlertProps } from '../../Shared/Utils/Alert';
import { useFormController } from '../../Shared/Component/readFormData/useFormController';
import {
  createSubscriptionPlanFromAdmin,
  deleteSubscriptionPlanByIdFromAdmin,
  updateSubscriptionPlanFromAdmin,
} from '../Services/SubscriptionPlanAdminService';
import { getConnectionTables, getDataConnections } from '../Services/DataService';
import { DataConnection, TableMeta } from '../Types/DataServiceType';
import {
  initialSubscriptionPlanAmdinRequest,
  PlanAccessPermission,
  PlanTableAccessMode,
  subscriptionPlanAdminRequest,
  subscriptionPlanAdminResponse,
} from '../Types/AdminSubscriptionPlanType';

/** The server refuses a longer allowlist, so the picker stops offering names at the same point. */
const MAX_PLAN_TABLES = 200;

interface PlanRow {
  id: string;
  name: string;
  price: number;
  trialDays: number;
  couponCode: string;
  discountAmount: number;
  discountPercent: number;
  active: boolean;
  isVisibleOnUI: boolean;
  isTrail: boolean;
  credits: number;
  amount: number;
  features: string[];
  createdAt: string;
  updatedAt: string;
  orderBy: number;
  isAddon: boolean;
  isDataService?: boolean;
  planCode?: string;
  dataServiceConnectionId?: string | null;
  dataServiceAllowQueryBuilder?: boolean;
  dataServiceAllowConsole?: boolean;
  dataServiceTableAccessMode?: PlanTableAccessMode;
  dataServiceTables?: string[];
  dataServicePermission?: PlanAccessPermission;
}

interface Props {
  data: subscriptionPlanAdminResponse[];
  onEditSubmit: (data: PlanRow) => void;
  onDeleteConfirm: (name: string) => void;
  onRegisterSubmit: (data: Partial<PlanRow>) => void;
  menuName: string;
  onChanged?: () => void;
}

const fieldClass =
  'w-full rounded-xl border border-slate-800 bg-slate-950 px-3.5 py-2.5 text-sm text-slate-200 placeholder-slate-600 outline-none transition [color-scheme:dark] focus:border-cyan-500/50';

const labelClass = 'mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-400';

const toNumber = (value: unknown): number => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const formatDate = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

const boolMeta = (value: boolean, yes: string, no: string) => ({
  text: value ? yes : no,
  cls: value
    ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400'
    : 'border-slate-700 bg-slate-950 text-slate-400',
});

const DarkField = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <label className={labelClass}>{label}</label>
    {children}
  </div>
);

const BoolSelect = ({
  label,
  value,
  onChange,
}: {
  label: string;
  value: boolean;
  onChange: (next: boolean) => void;
}) => (
  <DarkField label={label}>
    <select
      value={value ? 'true' : 'false'}
      onChange={(e) => onChange(e.target.value === 'true')}
      className={fieldClass}
    >
      <option value="true" className="bg-slate-900 text-slate-100">
        Yes
      </option>
      <option value="false" className="bg-slate-900 text-slate-100">
        No
      </option>
    </select>
  </DarkField>
);

const FeaturesEditor = ({
  features,
  onChange,
}: {
  features: string[];
  onChange: (next: string[]) => void;
}) => {
  const [draft, setDraft] = useState('');

  const addFeatures = () => {
    const items = draft
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
    if (!items.length) return;

    const existing = features.map((f) => f.toLowerCase());
    const unique = items.filter((item) => !existing.includes(item.toLowerCase()));
    onChange([...features, ...unique].slice(0, 10));
    setDraft('');
  };

  const removeFeature = (value: string) => {
    onChange(features.filter((item) => item !== value));
  };

  return (
    <div className="md:col-span-2">
      <label className={labelClass}>Premium Features ({features.length}/10)</label>
      <div className="flex flex-col gap-2 sm:flex-row">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addFeatures();
            }
          }}
          placeholder="Add features (comma separated)"
          className={fieldClass}
        />
        <button
          type="button"
          onClick={addFeatures}
          className="flex items-center justify-center gap-1.5 rounded-xl border border-cyan-500/30 bg-cyan-500/10 px-4 py-2.5 text-[11px] font-black uppercase tracking-widest text-cyan-400 transition hover:bg-cyan-500/20"
        >
          <Plus size={13} /> Add
        </button>
      </div>

      {features.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {features.map((feature) => (
            <span
              key={feature}
              className="flex items-center gap-1.5 rounded-full border border-slate-800 bg-slate-950 px-3 py-1.5 text-[11px] font-semibold text-slate-300"
            >
              {feature}
              <button
                type="button"
                onClick={() => removeFeature(feature)}
                className="text-slate-500 transition hover:text-rose-400"
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * What a purchase of this plan grants, shown only when the plan IS a Data Service plan.
 *
 * <p>This is the whole grant, stated once at save time. Nothing about it is decided when a payment
 * arrives, which is what lets a verified payment apply it without a human in the loop - and what
 * makes the Payment/Subscription Automation tab in Team Access explainable after the fact.
 */
const DataServiceGrantFields = ({
  values,
  connections,
  onChange,
}: {
  values: subscriptionPlanAdminRequest;
  connections: DataConnection[];
  onChange: (patch: Partial<subscriptionPlanAdminRequest>) => void;
}) => {
  const selectedConnection = connections.find((c) => c.id === values.dataServiceConnectionId);
  const consoleBlockedOnConnection = selectedConnection
    ? !(selectedConnection as DataConnection & { allowConsole?: boolean }).allowConsole
    : false;

  const tables = values.dataServiceTables ?? [];
  const connectionId = values.dataServiceConnectionId ?? '';

  /**
   * The table catalogue of the chosen connection, read from the database rather than
   * guessed at, so what the admin ticks is exactly what a buyer will be able to reach.
   */
  const [catalog, setCatalog] = useState<TableMeta[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState('');
  const [tableSearch, setTableSearch] = useState('');

  useEffect(() => {
    setCatalog([]);
    setCatalogError('');
    setTableSearch('');
    if (!connectionId) return;

    let cancelled = false;
    setCatalogLoading(true);
    getConnectionTables(connectionId)
      .then((res) => {
        if (cancelled) return;
        if (res.success && res.data) setCatalog(res.data.tables ?? []);
        else setCatalogError(res.message ?? 'Could not read the tables on this connection.');
      })
      .catch(() => {
        if (!cancelled) setCatalogError('Could not read the tables on this connection.');
      })
      .finally(() => {
        if (!cancelled) setCatalogLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [connectionId]);

  const selectedSet = useMemo(() => new Set(tables), [tables]);
  const catalogNames = useMemo(() => catalog.map((t) => t.tableName), [catalog]);

  /**
   * A saved entry the catalogue does not contain - a wildcard pattern, or a table that
   * has since been dropped. Shown rather than hidden, because ticking the list must not
   * quietly delete something an admin wrote on purpose.
   */
  const unmatched = useMemo(
    () => tables.filter((name) => !catalogNames.includes(name)),
    [tables, catalogNames]
  );

  const visibleTables = useMemo(() => {
    const needle = tableSearch.trim().toLowerCase();
    if (!needle) return catalog;
    return catalog.filter((t) => t.tableName.toLowerCase().includes(needle));
  }, [catalog, tableSearch]);

  const toggleTable = (name: string) =>
    onChange({
      dataServiceTables: selectedSet.has(name)
        ? tables.filter((item) => item !== name)
        : [...tables, name],
    });

  const removeTable = (name: string) =>
    onChange({ dataServiceTables: tables.filter((item) => item !== name) });

  /** The server caps an allowlist at 200 names, so the picker stops there too. */
  const atTableLimit = tables.length >= MAX_PLAN_TABLES;

  return (
    <div className="md:col-span-2 space-y-4 rounded-2xl border border-cyan-500/20 bg-cyan-500/[0.04] p-4">
      <div className="flex items-start gap-2">
        <Database size={15} className="mt-0.5 flex-shrink-0 text-cyan-400" />
        <div>
          <p className="text-[11px] font-black uppercase tracking-widest text-cyan-300">
            Data Service access granted by this plan
          </p>
          <p className="mt-1 text-[11px] leading-relaxed text-slate-400">
            Applied automatically to the whole company the moment a payment for this plan is
            verified. Leave a plan's settings untouched and its access stays exactly as configured
            here.
          </p>
        </div>
      </div>

      <DarkField label="Connection *">
        <select
          value={values.dataServiceConnectionId ?? ''}
          onChange={(e) => onChange({ dataServiceConnectionId: e.target.value })}
          className={fieldClass}
        >
          <option value="" className="bg-slate-900 text-slate-100">
            {connections.length ? 'Choose a connection' : 'No connections exist yet'}
          </option>
          {connections.map((connection) => (
            <option key={connection.id} value={connection.id} className="bg-slate-900 text-slate-100">
              {connection.alias} · {connection.databaseName} · {connection.environment}
            </option>
          ))}
        </select>
      </DarkField>

      <div>
        <label className={labelClass}>What they can use</label>
        <div className="grid gap-2 sm:grid-cols-2">
          {(
            [
              {
                key: 'dataServiceAllowQueryBuilder' as const,
                label: 'Query Builder',
                hint: 'Structured queries, filters and exports.',
              },
              {
                key: 'dataServiceAllowConsole' as const,
                label: 'SQL Console (raw SQL)',
                hint: 'Hand-written SQL against the connection.',
              },
            ] as const
          ).map((capability) => {
            const on = Boolean(values[capability.key]);
            return (
              <button
                key={capability.key}
                type="button"
                onClick={() => onChange({ [capability.key]: !on } as Partial<subscriptionPlanAdminRequest>)}
                className={`rounded-xl border p-3 text-left transition ${
                  on
                    ? 'border-cyan-500/40 bg-cyan-500/10'
                    : 'border-slate-800 bg-slate-950 hover:border-slate-700'
                }`}
              >
                <span className="flex items-center gap-2">
                  <span
                    className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border ${
                      on ? 'border-cyan-400 bg-cyan-500/20 text-cyan-300' : 'border-slate-700'
                    }`}
                  >
                    {on && <CheckCircle2 size={12} />}
                  </span>
                  <span className="text-xs font-black uppercase tracking-widest text-slate-200">
                    {capability.label}
                  </span>
                </span>
                <span className="mt-1 block text-[11px] leading-relaxed text-slate-500">
                  {capability.hint}
                </span>
              </button>
            );
          })}
        </div>
        {consoleBlockedOnConnection && values.dataServiceAllowConsole && (
          <p className="mt-2 flex items-start gap-1.5 text-[11px] leading-relaxed text-amber-400">
            <AlertTriangle size={13} className="mt-px flex-shrink-0" />
            This connection does not have the SQL console enabled, so buyers will get the Query
            Builder only until a super admin switches the console on for the connection itself.
          </p>
        )}
        {!values.dataServiceAllowQueryBuilder && !values.dataServiceAllowConsole && (
          <p className="mt-2 text-[11px] leading-relaxed text-amber-400">
            Both are off, so a verified payment will grant a company an access row that opens
            nothing. Pick at least the Query Builder.
          </p>
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <DarkField label="Tables">
          <select
            value={values.dataServiceTableAccessMode ?? 'ALL'}
            onChange={(e) =>
              onChange({ dataServiceTableAccessMode: e.target.value as PlanTableAccessMode })
            }
            className={fieldClass}
          >
            <option value="ALL" className="bg-slate-900 text-slate-100">
              All tables on the connection
            </option>
            <option value="SELECTED" className="bg-slate-900 text-slate-100">
              Only the tables listed
            </option>
          </select>
        </DarkField>

        <DarkField label="What they can do">
          <select
            value={values.dataServicePermission ?? 'READ'}
            onChange={(e) =>
              onChange({ dataServicePermission: e.target.value as PlanAccessPermission })
            }
            className={fieldClass}
          >
            <option value="READ" className="bg-slate-900 text-slate-100">
              Read only
            </option>
            <option value="READ_WRITE" className="bg-slate-900 text-slate-100">
              Read and write
            </option>
          </select>
        </DarkField>
      </div>

      {(values.dataServiceTableAccessMode ?? 'ALL') === 'SELECTED' && (
        <div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <label className={labelClass}>
              Tables this plan grants ({tables.length}/{MAX_PLAN_TABLES}) *
            </label>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() =>
                  onChange({
                    dataServiceTables: [
                      ...tables,
                      ...visibleTables
                        .map((t) => t.tableName)
                        .filter((name) => !selectedSet.has(name)),
                    ].slice(0, MAX_PLAN_TABLES),
                  })
                }
                disabled={catalogLoading || atTableLimit || visibleTables.length === 0}
                className="text-[10px] font-bold uppercase tracking-widest text-cyan-400 transition hover:text-cyan-300 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {tableSearch.trim() ? 'Select these results' : 'Select all'}
              </button>
              <button
                type="button"
                onClick={() => onChange({ dataServiceTables: [] })}
                disabled={tables.length === 0}
                className="text-[10px] font-bold uppercase tracking-widest text-slate-500 transition hover:text-rose-300 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Clear
              </button>
            </div>
          </div>

          {!connectionId ? (
            <p className="rounded-xl border border-slate-800 bg-slate-950 px-3 py-2.5 text-[11px] text-slate-500">
              Choose a connection above and its tables will be listed here.
            </p>
          ) : catalogLoading ? (
            <p className="flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-950 px-3 py-2.5 text-[11px] text-slate-400">
              <Loader2 size={13} className="animate-spin" /> Reading the tables on this connection…
            </p>
          ) : catalogError ? (
            <p className="flex items-start gap-2 rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2.5 text-[11px] leading-relaxed text-amber-300">
              <AlertTriangle size={13} className="mt-0.5 shrink-0" />
              {catalogError} The connection may still be reachable from the Data Service itself.
            </p>
          ) : catalog.length === 0 ? (
            <p className="rounded-xl border border-slate-800 bg-slate-950 px-3 py-2.5 text-[11px] text-slate-500">
              This connection reports no tables. Check the connection before saving a plan that
              depends on it.
            </p>
          ) : (
            <>
              <div className="relative mt-2">
                <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  type="text"
                  value={tableSearch}
                  onChange={(e) => setTableSearch(e.target.value)}
                  placeholder="Filter tables…"
                  className="w-full rounded-xl border border-slate-800 bg-slate-950 py-2 pl-9 pr-3 text-xs text-slate-200 outline-none focus:border-cyan-500/50"
                />
              </div>

              <div className="mt-2 max-h-64 overflow-y-auto rounded-xl border border-slate-800 bg-slate-950">
                {visibleTables.length === 0 ? (
                  <p className="px-3 py-2.5 text-[11px] text-slate-500">
                    No table matches “{tableSearch.trim()}”.
                  </p>
                ) : (
                  visibleTables.map((table) => {
                    const checked = selectedSet.has(table.tableName);
                    return (
                      <label
                        key={table.tableName}
                        className={`flex cursor-pointer items-center gap-2.5 border-b border-slate-800/60 px-3 py-2 text-xs transition last:border-b-0 ${
                          checked ? 'bg-cyan-500/[0.06]' : 'hover:bg-slate-900/60'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={!checked && atTableLimit}
                          onChange={() => toggleTable(table.tableName)}
                          className="accent-cyan-500"
                        />
                        <span className="flex-1 truncate text-slate-200">{table.tableName}</span>
                        <span className="shrink-0 text-[10px] text-slate-500">
                          {table.tableSchema ? `${table.tableSchema} · ` : ''}
                          {table.columnCount} cols
                          {table.rowCountIsExact ? ` · ${table.rowCount.toLocaleString()} rows` : ''}
                        </span>
                      </label>
                    );
                  })
                )}
              </div>
            </>
          )}

          {unmatched.length > 0 && (
            <div className="mt-2">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                Saved entries not in this connection&apos;s list
              </p>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {unmatched.map((name) => (
                  <span
                    key={name}
                    className="inline-flex items-center gap-1 rounded-lg border border-amber-500/30 bg-amber-500/10 px-2 py-1 text-[10px] text-amber-200"
                  >
                    {name}
                    <button
                      type="button"
                      onClick={() => removeTable(name)}
                      title="Remove"
                      className="text-amber-400/70 transition hover:text-rose-300"
                    >
                      <X size={11} />
                    </button>
                  </span>
                ))}
              </div>
              <p className="mt-1.5 text-[11px] leading-relaxed text-slate-500">
                Kept exactly as saved. Wildcards such as <code>public.jobs_*</code> still work, but
                an exact name is safer: a buyer&apos;s grant is stored as written.
              </p>
            </div>
          )}

          <p className="mt-2 text-[11px] leading-relaxed text-slate-500">
            A payment for this plan grants a company only the tables ticked here.
          </p>
        </div>
      )}
    </div>
  );
};

const StatCard = ({
  label,
  value,
  icon,
  accent,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
  accent: string;
}) => (
  <div className="relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
    <div className="flex items-center justify-between">
      <span className={`flex h-9 w-9 items-center justify-center rounded-xl border ${accent}`}>{icon}</span>
      <span className="text-2xl font-black text-slate-100">{value}</span>
    </div>
    <p className="mt-2 text-[11px] font-bold uppercase tracking-widest text-slate-400">{label}</p>
  </div>
);

const SubscriptionPlanByAdminComponent = ({
  data,
  onEditSubmit,
  onDeleteConfirm,
  onRegisterSubmit,
  menuName,
  onChanged,
}: Props) => {
  const { values, setAllValues, handleInputChange, handleValueChange } =
    useFormController<subscriptionPlanAdminRequest>(initialSubscriptionPlanAmdinRequest);

  const [selectedPlan, setSelectedPlan] = useState<PlanRow | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<PlanRow | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [alert, setAlert] = useState<AlertProps | null>(null);

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [connections, setConnections] = useState<DataConnection[]>([]);

  /**
   * Loaded once for the whole screen rather than per form open: the plan form needs the list to
   * offer a connection, and refetching it each time would make the dropdown briefly empty on save.
   */
  useEffect(() => {
    let cancelled = false;
    getDataConnections({}, 0, 100)
      .then((response) => {
        if (cancelled || !response.success || !response.data) return;
        setConnections(response.data.page?.content ?? []);
      })
      .catch(() => {
        /* The dropdown shows its empty state; the save is rejected server-side too. */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const rows = useMemo(() => data as unknown as PlanRow[], [data]);

  const stats = useMemo(
    () => ({
      total: rows.length,
      active: rows.filter((row) => row.active).length,
      trial: rows.filter((row) => row.isTrail).length,
      addOn: rows.filter((row) => row.isAddon).length,
      dataService: rows.filter((row) => row.isDataService).length,
    }),
    [rows]
  );

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter((row) => {
      const matchesSearch =
        !term ||
        row.name?.toLowerCase().includes(term) ||
        row.couponCode?.toLowerCase().includes(term) ||
        row.planCode?.toLowerCase().includes(term);

      const matchesType =
        typeFilter === 'ALL' ||
        (typeFilter === 'TRIAL' && row.isTrail) ||
        (typeFilter === 'ADDON' && row.isAddon) ||
        (typeFilter === 'DATASERVICE' && row.isDataService) ||
        (typeFilter === 'STANDARD' && !row.isTrail && !row.isAddon && !row.isDataService);

      const matchesStatus =
        statusFilter === 'ALL' ||
        (statusFilter === 'ACTIVE' && row.active) ||
        (statusFilter === 'INACTIVE' && !row.active);

      return matchesSearch && matchesType && matchesStatus;
    });
  }, [rows, search, typeFilter, statusFilter]);

  const hasActiveFilters = Boolean(search) || typeFilter !== 'ALL' || statusFilter !== 'ALL';

  const resetFilters = () => {
    setSearch('');
    setTypeFilter('ALL');
    setStatusFilter('ALL');
  };

  const buildPayload = (): subscriptionPlanAdminRequest => ({
    ...values,
    // A number, never "": an empty string binds to null server-side, and a null price reaches the
    // customer pricing page, where every amount is formatted or multiplied unguarded.
    price: toNumber(values.price),
    trialDays: toNumber(values.trialDays),
    discountAmount: toNumber(values.discountAmount),
    discountPercent: toNumber(values.discountPercent),
    credits: toNumber(values.credits),
    amount: toNumber(values.amount),
    orderBy: toNumber(values.orderBy),
    features: values.features ?? [],
  });

  const openCreate = () => {
    setAllValues(initialSubscriptionPlanAmdinRequest);
    setSelectedPlan(null);
    setFormMode('create');
    setFormOpen(true);
  };

  const openEdit = (plan: PlanRow) => {
    setAllValues({
      name: plan.name || '',
      price: plan.price?.toString() || '',
      trialDays: plan.trialDays || 0,
      couponCode: plan.couponCode || '',
      discountAmount: plan.discountAmount || 0,
      discountPercent: plan.discountPercent || 0,
      active: plan.active,
      isVisibleOnUI: plan.isVisibleOnUI,
      isTrail: plan.isTrail,
      credits: plan.credits || 0,
      amount: plan.amount || 0,
      features: plan.features || [],
      orderBy: plan.orderBy || 0,
      isAddon: plan.isAddon || false,
      isDataService: plan.isDataService || false,
      // Round-tripped explicitly: without this, editing a Data Service plan would blank the
      // connection and drop the grant template on the very next save.
      dataServiceConnectionId: plan.dataServiceConnectionId ?? '',
      dataServiceAllowQueryBuilder: plan.dataServiceAllowQueryBuilder ?? true,
      dataServiceAllowConsole: plan.dataServiceAllowConsole ?? false,
      dataServiceTableAccessMode: plan.dataServiceTableAccessMode ?? 'ALL',
      dataServiceTables: plan.dataServiceTables ?? [],
      dataServicePermission: plan.dataServicePermission ?? 'READ',
    });
    setSelectedPlan(plan);
    setFormMode('edit');
    setFormOpen(true);
  };

  const handleSubmit = async () => {
    if (!values.name.trim()) {
      setAlert({
        variant: 'error',
        title: 'Validation',
        message: `${menuName} name is required.`,
        autoCloseMs: 5000,
        onClose: () => setAlert(null),
      });
      return;
    }

    // Caught here because a plan saved without one becomes an unchargeable, unpriceable row that
    // only fails later, on a customer's screen.
    const price = Number(values.price);
    if (values.price === '' || values.price === null || !Number.isFinite(price) || price < 0) {
      setAlert({
        variant: 'error',
        title: 'Validation',
        message: 'Enter a price in rupees. Use 0 for a free plan.',
        autoCloseMs: 6000,
        onClose: () => setAlert(null),
      });
      return;
    }

    // Checked here rather than only on the server so the admin is told what is missing while the
    // form is still in front of them. The server enforces all of it regardless.
    if (values.isDataService) {
      const dataServiceProblem =
        !values.dataServiceConnectionId
          ? 'Choose the connection this plan grants.'
          : !values.dataServiceAllowQueryBuilder && !values.dataServiceAllowConsole
            ? 'Turn on the Query Builder or the SQL Console, otherwise a payment grants access to nothing.'
            : (values.dataServiceTableAccessMode ?? 'ALL') === 'SELECTED' &&
                (values.dataServiceTables ?? []).length === 0
              ? 'List the tables this plan grants, or switch it to all tables.'
              : null;

      if (dataServiceProblem) {
        setAlert({
          variant: 'error',
          title: 'Data Service settings',
          message: dataServiceProblem,
          autoCloseMs: 6000,
          onClose: () => setAlert(null),
        });
        return;
      }
    }

    setSaving(true);
    setAlert(null);
    try {
      const payload = buildPayload();
      const response =
        formMode === 'create'
          ? await createSubscriptionPlanFromAdmin(payload)
          : await updateSubscriptionPlanFromAdmin(payload, selectedPlan?.id || '');

      if (response.success) {
        setAlert({
          variant: 'success',
          title: 'Success',
          message:
            response.message ||
            (formMode === 'create'
              ? `${menuName} created successfully.`
              : `${menuName} updated successfully.`),
          autoCloseMs: 5000,
          onClose: () => setAlert(null),
        });
        setFormOpen(false);
        onChanged?.();
        if (formMode === 'create') {
          onRegisterSubmit({});
        } else if (selectedPlan) {
          onEditSubmit(selectedPlan);
        }
      } else {
        setAlert({
          variant: 'error',
          title: 'Error',
          message: response.message || `Could not save the ${menuName}.`,
          autoCloseMs: 5000,
          onClose: () => setAlert(null),
        });
      }
    } catch (err) {
      console.error('Failed to save subscription plan', err);
      setAlert({
        variant: 'error',
        title: 'System error',
        message: 'Something went wrong. Please try again.',
        autoCloseMs: 5000,
        onClose: () => setAlert(null),
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setAlert(null);
    try {
      const response = await deleteSubscriptionPlanByIdFromAdmin(deleteTarget.id);
      if (response.success) {
        setAlert({
          variant: 'success',
          title: 'Deleted',
          message: `${deleteTarget.name} has been deleted successfully.`,
          autoCloseMs: 5000,
          onClose: () => setAlert(null),
        });
        onDeleteConfirm(deleteTarget.name);
        setDeleteTarget(null);
        onChanged?.();
      } else {
        setAlert({
          variant: 'error',
          title: 'Delete Error',
          message: response.message || `Could not delete the ${menuName}.`,
          autoCloseMs: 5000,
          onClose: () => setAlert(null),
        });
      }
    } catch (err) {
      console.error('Failed to delete subscription plan', err);
      setAlert({
        variant: 'error',
        title: 'System error',
        message: 'Something went wrong. Please try again.',
        autoCloseMs: 5000,
        onClose: () => setAlert(null),
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 p-4 text-slate-100 md:p-8">
      {alert && <Alert {...alert} />}

      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
            <CreditCard size={22} />
          </span>
          <div>
            <h1 className="text-xl font-black tracking-tight text-slate-100 md:text-2xl">{menuName}</h1>
            <p className="text-xs font-semibold text-slate-400 md:text-sm">
              Create and manage the plans available on the platform
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => onChanged?.()}
            className="flex items-center justify-center gap-2 rounded-xl border border-slate-800 bg-slate-900 px-4 py-2.5 text-xs font-black uppercase tracking-widest text-cyan-400 transition hover:border-cyan-500/40"
          >
            <RefreshCw size={15} /> Refresh
          </button>
          <button
            onClick={openCreate}
            className="flex items-center justify-center gap-2 rounded-xl bg-cyan-500 px-4 py-2.5 text-xs font-black uppercase tracking-widest text-slate-950 transition hover:bg-cyan-400"
          >
            <Plus size={15} /> Add {menuName}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard
          label="Total Plans"
          value={stats.total}
          icon={<CreditCard size={16} />}
          accent="border-cyan-500/30 bg-cyan-500/10 text-cyan-400"
        />
        <StatCard
          label="Active"
          value={stats.active}
          icon={<CheckCircle2 size={16} />}
          accent="border-emerald-500/30 bg-emerald-500/10 text-emerald-400"
        />
        <StatCard
          label="Trial Plans"
          value={stats.trial}
          icon={<Sparkles size={16} />}
          accent="border-amber-500/30 bg-amber-500/10 text-amber-400"
        />
        <StatCard
          label="Add-on / Top-up"
          value={stats.addOn}
          icon={<PlusCircle size={16} />}
          accent="border-violet-500/30 bg-violet-500/10 text-violet-400"
        />
      </div>

      {/* Filters */}
      <div className="mb-6 rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
        <div className="mb-3 flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-slate-400">
          <Filter size={14} /> Filters
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div className="relative md:col-span-2">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by plan name, code or coupon..."
              className="w-full rounded-xl border border-slate-800 bg-slate-950 py-2.5 pl-9 pr-9 text-sm text-slate-200 placeholder-slate-600 outline-none transition focus:border-cyan-500/50"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
              >
                <X size={15} />
              </button>
            )}
          </div>

          <div className="relative">
            <Tag size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full appearance-none rounded-xl border border-slate-800 bg-slate-950 py-2.5 pl-9 pr-8 text-sm text-slate-200 outline-none transition [color-scheme:dark] focus:border-cyan-500/50"
            >
              <option value="ALL" className="bg-slate-900 text-slate-100">
                All Types
              </option>
              <option value="STANDARD" className="bg-slate-900 text-slate-100">
                Standard
              </option>
              <option value="TRIAL" className="bg-slate-900 text-slate-100">
                Trial
              </option>
              <option value="ADDON" className="bg-slate-900 text-slate-100">
                Add-on / Top-up
              </option>
              <option value="DATASERVICE" className="bg-slate-900 text-slate-100">
                Data Service
              </option>
            </select>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-3">
          {[
            { label: 'All Status', value: 'ALL' },
            { label: 'Active', value: 'ACTIVE' },
            { label: 'Inactive', value: 'INACTIVE' },
          ].map((option) => (
            <button
              key={option.value}
              onClick={() => setStatusFilter(option.value)}
              className={`rounded-full border px-3 py-1.5 text-[10px] font-black uppercase tracking-widest transition ${
                statusFilter === option.value
                  ? 'border-cyan-500/40 bg-cyan-500/10 text-cyan-300'
                  : 'border-slate-800 bg-slate-950 text-slate-400 hover:text-slate-200'
              }`}
            >
              {option.label}
            </button>
          ))}

          {hasActiveFilters && (
            <button
              onClick={resetFilters}
              className="ml-auto flex items-center gap-1.5 rounded-full border border-slate-800 bg-slate-950 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-rose-400 hover:border-rose-500/40"
            >
              <X size={12} /> Clear
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      {rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-700 bg-slate-900/30 py-20 text-center">
          <span className="mb-2 block text-2xl">💳</span>
          <p className="text-xs font-black uppercase tracking-widest text-slate-400">No plans found</p>
          <button onClick={openCreate} className="mt-3 text-xs font-bold text-cyan-400 hover:underline">
            Create your first {menuName}
          </button>
        </div>
      ) : filteredRows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-700 bg-slate-900/30 py-20 text-center">
          <span className="mb-2 block text-2xl">🔍</span>
          <p className="text-xs font-black uppercase tracking-widest text-slate-400">No matching plans</p>
          <button onClick={resetFilters} className="mt-3 text-xs font-bold text-cyan-400 hover:underline">
            Clear filters
          </button>
        </div>
      ) : (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
          <div className="overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/40">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-800 bg-slate-900/70 px-5 py-4">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-800 bg-slate-950 text-cyan-400">
                  <CreditCard size={18} />
                </span>
                <div>
                  <h3 className="text-sm font-black tracking-tight text-slate-100 md:text-base">{menuName} Directory</h3>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                    {filteredRows.length} item{filteredRows.length === 1 ? '' : 's'}
                  </p>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[1180px] border-collapse text-left">
                <thead>
                  <tr className="border-b border-slate-800 text-[10px] font-black uppercase tracking-widest text-slate-500">
                    <th className="px-5 py-3">Plan</th>
                    <th className="px-5 py-3">Credits</th>
                    <th className="px-5 py-3">Order</th>
                    <th className="px-5 py-3">Price</th>
                    <th className="px-5 py-3">Discount</th>
                    <th className="px-5 py-3">Trial</th>
                    <th className="px-5 py-3">Add-on</th>
                    <th className="px-5 py-3">Visible</th>
                    <th className="px-5 py-3">Status</th>
                    <th className="px-5 py-3">Created</th>
                    <th className="px-5 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((plan) => {
                    const trial = boolMeta(plan.isTrail, 'Yes', 'No');
                    const addOn = boolMeta(plan.isAddon, 'Yes', 'No');
  const dataService = boolMeta(plan.isDataService ?? false, 'Yes', 'No');
                    const visible = boolMeta(plan.isVisibleOnUI, 'Yes', 'No');
                    return (
                      <tr
                        key={plan.id}
                        className="border-b border-slate-800/60 transition last:border-0 hover:bg-slate-800/20"
                      >
                        <td className="px-5 py-4">
                          <p className="text-sm font-bold text-slate-100">{plan.name || '—'}</p>
                          <p className="mt-0.5 flex items-center gap-1.5 text-[11px] font-semibold text-slate-500">
                            <Tag size={11} />
                            {plan.planCode || plan.couponCode || '—'}
                          </p>
                        </td>

                        <td className="px-5 py-4">
                          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-300">
                            <Ticket size={12} className="text-slate-500" />
                            {plan.credits || 0}
                          </span>
                        </td>

                        <td className="px-5 py-4 text-xs font-semibold text-slate-300">{plan.orderBy || 0}</td>

                        <td className="px-5 py-4">
                          <span className="text-sm font-bold text-slate-100">
                            {plan.price === 0 ? 'Free' : `₹${Number(plan.price || 0).toLocaleString('en-IN')}`}
                          </span>
                        </td>

                        <td className="px-5 py-4">
                          <p className="text-sm font-bold text-slate-100">
                            ₹{Number(plan.discountAmount || 0).toLocaleString('en-IN')}
                          </p>
                          <p className="mt-0.5 text-[11px] font-semibold text-slate-500">
                            {plan.discountPercent || 0}% off
                          </p>
                        </td>

                        <td className="px-5 py-4">
                          <span
                            className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-widest ${trial.cls}`}
                          >
                            {trial.text}
                          </span>
                          <p className="mt-1 text-[11px] font-semibold text-slate-500">{plan.trialDays || 0} days</p>
                        </td>

                        <td className="px-5 py-4">
                          <span
                            className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-widest ${addOn.cls}`}
                          >
                            {addOn.text} • Add-on
                          </span>
                          <span
                            className={`mt-1.5 inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-widest ${dataService.cls}`}
                          >
                            {dataService.text} • Data Service
                          </span>
                        </td>

                        <td className="px-5 py-4">
                          <span
                            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-widest ${visible.cls}`}
                          >
                            {plan.isVisibleOnUI ? <Eye size={11} /> : <EyeOff size={11} />}
                            {visible.text}
                          </span>
                        </td>

                        <td className="px-5 py-4">
                          <span
                            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-widest ${
                              plan.active
                                ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400'
                                : 'border-rose-500/30 bg-rose-500/10 text-rose-400'
                            }`}
                          >
                            <span className="h-1.5 w-1.5 rounded-full bg-current" />
                            {plan.active ? 'Active' : 'Inactive'}
                          </span>
                        </td>

                        <td className="px-5 py-4">
                          <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-300">
                            <CalendarDays size={12} className="text-slate-500" />
                            {formatDate(plan.createdAt)}
                          </span>
                        </td>

                        <td className="px-5 py-4">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => openEdit(plan)}
                              title="Edit plan"
                              className="flex items-center gap-1.5 rounded-lg border border-slate-800 bg-slate-950 px-3 py-1.5 text-[11px] font-black uppercase tracking-widest text-cyan-400 transition hover:border-cyan-500/40"
                            >
                              <Pencil size={12} /> Edit
                            </button>
                            <button
                              onClick={() => setDeleteTarget(plan)}
                              className="flex items-center gap-1.5 rounded-lg border border-slate-800 bg-slate-950 px-3 py-1.5 text-[11px] font-black uppercase tracking-widest text-rose-400 transition hover:border-rose-500/40"
                            >
                              <Trash2 size={12} /> Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </motion.div>
      )}

      {/* Create / Edit modal */}
      <AnimatePresence>
        {formOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-950/70 p-4 backdrop-blur-sm"
            onClick={() => !saving && setFormOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.98 }}
              onClick={(e) => e.stopPropagation()}
              className="my-8 w-full max-w-2xl rounded-2xl border border-slate-800 bg-slate-900 shadow-2xl"
            >
              <div className="flex items-center justify-between border-b border-slate-800 px-6 py-4">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
                    <CreditCard size={18} />
                  </span>
                  <div>
                    <h2 className="text-base font-black tracking-tight text-slate-100">
                      {formMode === 'create' ? `Create New ${menuName}` : `Edit ${selectedPlan?.name || menuName}`}
                    </h2>
                    <p className="text-[11px] font-semibold text-slate-500">
                      {formMode === 'create'
                        ? `Add a new ${menuName} to the platform`
                        : 'Update the details of this plan'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => !saving && setFormOpen(false)}
                  className="rounded-lg p-1.5 text-slate-500 transition hover:bg-slate-800 hover:text-slate-200"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="grid grid-cols-1 gap-4 px-6 py-5 md:grid-cols-2">
                <DarkField label={`${menuName} Name *`}>
                  <input
                    type="text"
                    value={values.name}
                    onChange={handleInputChange('name')}
                    placeholder={`${menuName} name`}
                    className={fieldClass}
                  />
                </DarkField>

                <DarkField label="Price (INR)">
                  <input
                    type="number"
                    value={values.price}
                    onChange={handleInputChange('price')}
                    placeholder="0"
                    className={fieldClass}
                  />
                </DarkField>

                <DarkField label="Trial Days">
                  <input
                    type="number"
                    value={values.trialDays?.toString() || ''}
                    onChange={handleInputChange('trialDays')}
                    placeholder="0"
                    className={fieldClass}
                  />
                </DarkField>

                <DarkField label="Credits">
                  <input
                    type="number"
                    value={values.credits?.toString() || ''}
                    onChange={handleInputChange('credits')}
                    placeholder="0"
                    className={fieldClass}
                  />
                </DarkField>

                <DarkField label="Coupon Code">
                  <input
                    type="text"
                    value={values.couponCode}
                    onChange={handleInputChange('couponCode')}
                    placeholder="Coupon code"
                    className={fieldClass}
                  />
                </DarkField>

                <DarkField label="Discount Percent">
                  <input
                    type="number"
                    value={values.discountPercent?.toString() || ''}
                    onChange={handleInputChange('discountPercent')}
                    placeholder="0"
                    className={fieldClass}
                  />
                </DarkField>

                {formMode === 'edit' && (
                  <>
                    <DarkField label="Discount Amount (INR)">
                      <input
                        type="number"
                        value={values.discountAmount?.toString() || ''}
                        onChange={handleInputChange('discountAmount')}
                        placeholder="0"
                        className={fieldClass}
                      />
                    </DarkField>

                    <DarkField label="Amount (INR)">
                      <input
                        type="number"
                        value={values.amount?.toString() || ''}
                        onChange={handleInputChange('amount')}
                        placeholder="0"
                        className={fieldClass}
                      />
                    </DarkField>
                  </>
                )}

                <DarkField label="Display Order">
                  <input
                    type="number"
                    value={values.orderBy?.toString() || ''}
                    onChange={handleInputChange('orderBy')}
                    placeholder="0"
                    className={fieldClass}
                  />
                </DarkField>

                <BoolSelect
                  label="Plan Status"
                  value={values.active}
                  onChange={handleValueChange('active')}
                />

                <BoolSelect
                  label="Is Trial"
                  value={values.isTrail}
                  onChange={handleValueChange('isTrail')}
                />

                <BoolSelect
                  label="Visible on UI"
                  value={values.isVisibleOnUI}
                  onChange={handleValueChange('isVisibleOnUI')}
                />

<BoolSelect
              label="Is Add-on"
              value={values.isAddon}
              onChange={handleValueChange('isAddon')}
            />
            <BoolSelect
              label="Is Data Service"
              value={values.isDataService}
              onChange={handleValueChange('isDataService')}
            />

            {values.isDataService && (
              <DataServiceGrantFields
                values={values}
                connections={connections}
                onChange={(patch) => setAllValues({ ...values, ...patch })}
              />
            )}

                <FeaturesEditor
                  features={values.features ?? []}
                  onChange={(next) => setAllValues({ ...values, features: next })}
                />
              </div>

              <div className="flex items-center justify-end gap-3 border-t border-slate-800 px-6 py-4">
                <button
                  type="button"
                  onClick={() => setFormOpen(false)}
                  disabled={saving}
                  className="rounded-xl px-4 py-2.5 text-xs font-black uppercase tracking-widest text-slate-400 transition hover:text-slate-200 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={saving}
                  className="flex items-center gap-2 rounded-xl bg-cyan-500 px-5 py-2.5 text-xs font-black uppercase tracking-widest text-slate-950 transition hover:bg-cyan-400 disabled:opacity-50"
                >
                  {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
                  {saving ? 'Saving' : formMode === 'create' ? `Create ${menuName}` : 'Save Changes'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete confirmation */}
      <AnimatePresence>
        {deleteTarget && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm"
            onClick={() => !deleting && setDeleteTarget(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-2xl"
            >
              <div className="flex items-start gap-4">
                <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl border border-rose-500/30 bg-rose-500/10 text-rose-400">
                  <AlertTriangle size={20} />
                </span>
                <div>
                  <h3 className="text-base font-black tracking-tight text-slate-100">Delete {menuName}?</h3>
                  <p className="mt-1 text-sm text-slate-400">
                    This will permanently remove{' '}
                    <span className="font-bold text-slate-200">"{deleteTarget.name}"</span>. This action cannot be
                    undone.
                  </p>
                </div>
              </div>

              <div className="mt-6 flex items-center justify-end gap-3">
                <button
                  onClick={() => setDeleteTarget(null)}
                  disabled={deleting}
                  className="rounded-xl px-4 py-2.5 text-xs font-black uppercase tracking-widest text-slate-400 transition hover:text-slate-200 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="flex items-center gap-2 rounded-xl bg-rose-500 px-5 py-2.5 text-xs font-black uppercase tracking-widest text-white transition hover:bg-rose-400 disabled:opacity-50"
                >
                  {deleting ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                  {deleting ? 'Deleting' : 'Delete'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SubscriptionPlanByAdminComponent;
